import { Injectable } from '@angular/core';
import {
  createStore,
  filterNil,
  select,
  setProps,
  withProps,
} from '@ngneat/elf';
import {
  addEntities,
  getActiveEntity,
  getAllEntities,
  hasEntity,
  selectActiveEntity,
  selectAllEntities,
  selectEntity,
  setActiveId,
  setEntities,
  UIEntitiesRef,
  updateEntities,
  withActiveId,
  withEntities,
  withUIEntities,
} from '@ngneat/elf-entities';
import { map } from 'rxjs';
import { ListCompany } from '../models/list-company.model';
import { SearchParams } from '../models/search-params.model';
import { write } from './mutations';

export type SearchResultsUI = {
  id: string;
  showAllWorksitesParameter: boolean;
  isCollapsed: boolean;
};

const store = createStore(
  { name: 'searchResults' },
  withEntities<ListCompany, 'providerId'>({ idKey: 'providerId' }),
  withActiveId(),
  withUIEntities<SearchResultsUI>(),
  withProps<{
    totalSearchAmount: number;
    isSearching: boolean;
    searchParams: SearchParams;
    bisnodeNotResponding: boolean;
    extendedWorksites: string[];
    unavailableWorksites: string[];
  }>({
    totalSearchAmount: -1,
    isSearching: false,
    searchParams: undefined,
    bisnodeNotResponding: false,
    extendedWorksites: [],
    unavailableWorksites: [],
  }),
);

@Injectable({ providedIn: 'root' })
export class SearchResultsRepository {
  searchResults$ = store.pipe(selectAllEntities());
  active$ = store.pipe(selectActiveEntity());
  extendedWorksites$ = store.pipe(select((state) => state.extendedWorksites));
  isSearching$ = store.pipe(select((state) => state.isSearching));
  unavailableWorksites$ = store.pipe(
    select((state) => state.unavailableWorksites),
  );

  getTotalSearchAmount() {
    return store.query((state) => state.totalSearchAmount);
  }

  getBisnodeNotResponding() {
    return store.query((state) => state.bisnodeNotResponding);
  }

  getExtendedWorksites() {
    return store.query((state) => state.extendedWorksites);
  }

  getSearchParams() {
    return store.query((state) => state.searchParams);
  }

  getAll() {
    return store.query(getAllEntities());
  }

  getActive() {
    return store.query(getActiveEntity());
  }

  selectUi(
    providerId: string,
    uiProperty: 'showAllWorksitesParameter' | 'isCollapsed',
  ) {
    return store.pipe(selectEntity(providerId, { ref: UIEntitiesRef })).pipe(
      filterNil(),
      map((ui) => ui[uiProperty]),
    );
  }

  set(searchResult: ListCompany[]) {
    store.update(setEntities(searchResult));
    for (const company of searchResult) {
      this.addUi(company.providerId);
    }
  }

  setActive(providerId: string) {
    store.update(setActiveId(providerId));
  }

  setWorksiteIsUnavailable(providerId: string) {
    store.update(
      setProps(
        write(
          (state) =>
            (state.unavailableWorksites = [
              ...state.unavailableWorksites,
              providerId,
            ]),
        ),
      ),
    );
  }

  resetUnavailableWorksites() {
    store.update(setProps(write((state) => (state.unavailableWorksites = []))));
  }

  update(providerId: string, updater: (state: ListCompany) => void) {
    store.update(updateEntities(providerId, write<ListCompany>(updater)));
  }

  updateUi(providerId: string, updater: (state: SearchResultsUI) => void) {
    store.update(
      updateEntities(providerId, write<SearchResultsUI>(updater), {
        ref: UIEntitiesRef,
      }),
    );
  }

  setProps(
    props: Partial<{
      totalSearchAmount: number;
      isSearching: boolean;
      searchParams: SearchParams;
      bisnodeNotResponding: boolean;
      extendedWorksites: string[];
    }>,
  ) {
    store.update(
      setProps(
        write((state) => {
          state.totalSearchAmount =
            props.totalSearchAmount === undefined
              ? state.totalSearchAmount
              : props.totalSearchAmount;
          state.isSearching =
            props.isSearching === undefined
              ? state.isSearching
              : props.isSearching;
          state.searchParams =
            props.searchParams === undefined
              ? state.searchParams
              : props.searchParams;
          state.bisnodeNotResponding =
            props.bisnodeNotResponding === undefined
              ? state.bisnodeNotResponding
              : props.bisnodeNotResponding;
          state.extendedWorksites =
            props.extendedWorksites === undefined
              ? state.extendedWorksites
              : props.extendedWorksites;
        }),
      ),
    );
  }

  private addUi(providerId: string) {
    if (store.query(hasEntity(providerId, { ref: UIEntitiesRef }))) {
      return;
    }
    store.update(
      addEntities(
        {
          id: providerId,
          isCollapsed: false,
          showAllWorksitesParameter: true,
        },
        { ref: UIEntitiesRef },
      ),
    );
  }
}
